// ═══════════════════════════════════════════════════════════════
//   V19.4 — Standardized KPI Library
// ═══════════════════════════════════════════════════════════════
//
// Twelve canonical KPIs derived from the existing buildIncomeStatement,
// buildBalanceSheet, and buildT12 outputs. Each KPI is industry-agnostic
// and computed from the canonical mapping — no per-client config needed.
//
// Card shape: { id, label, group, value, valueFormatted, sub, status,
//               sparkline?: number[], n_a?: string }
//   status: 'good' | 'warn' | 'bad' | 'neutral'
//   n_a:    populated when a required canonical line is missing or $0,
//           so the card explains why instead of showing a misleading 0.
//
// Group taxonomy:
//   liquidity | profitability | efficiency | growth

// Round-to-integer convenience.
// eslint-disable-next-line no-unused-vars
function round0(x) { return Math.round(x); }

// ── Threshold defaults ─────────────────────────────────────────
// Conservative, industry-agnostic defaults. Editable per-client later
// via a future "KPI Targets" UI; for now we ship sensible defaults so
// the cards show a meaningful color out of the box.
const THRESHOLDS = {
  current_ratio:  { good: 1.5, warn: 1.0 }, // > good = green
  quick_ratio:    { good: 1.0, warn: 0.7 },
  debt_to_equity: { good: 1.0, warn: 2.0, inverse: true }, // lower is better
  gross_margin:   { good: 0.40, warn: 0.20 },
  operating_margin: { good: 0.15, warn: 0.05 },
  net_margin:     { good: 0.10, warn: 0.03 },
  ebitda_margin:  { good: 0.15, warn: 0.05 },
  ar_days:        { good: 30, warn: 45, inverse: true },
  ap_days:        { good: 45, warn: 30 }, // higher AP days (within reason) preserves cash
  cash_runway:    { good: 6, warn: 3 }, // months
  revenue_growth: { good: 0.05, warn: -0.05 },
};

function scoreThreshold(value, key) {
  const t = THRESHOLDS[key];
  if (!t || value == null || Number.isNaN(value)) return 'neutral';
  if (t.inverse) {
    if (value <= t.good) return 'good';
    if (value <= t.warn) return 'warn';
    return 'bad';
  }
  if (value >= t.good) return 'good';
  if (value >= t.warn) return 'warn';
  return 'bad';
}

const fmtPct = (v, digits = 1) =>
  v == null || Number.isNaN(v) ? 'n/a' : `${(v * 100).toFixed(digits)}%`;
const fmtRatio = (v, digits = 2) =>
  v == null || Number.isNaN(v) ? 'n/a' : v.toFixed(digits);
const fmtMoney = (v) => {
  if (v == null || Number.isNaN(v)) return 'n/a';
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};
const fmtDays = (v) => (v == null || Number.isNaN(v) ? 'n/a' : `${Math.round(v)} days`);
const fmtMonths = (v) => (v == null || Number.isNaN(v) ? 'n/a' : `${v.toFixed(1)} mo`);

// Derive the period day count from a buildIncomeStatement period descriptor.
// Default to 120 days (4 months) for the SMJ engagement as fallback.
function periodDays(periodLabel) {
  if (!periodLabel) return 120;
  const m = /(\d+)\s+months?/i.exec(periodLabel);
  if (m) return parseInt(m[1], 10) * 30;
  return 120;
}

// ── Single export ──────────────────────────────────────────────
// computeKpis(reports) → array of 12 KPI cards.
//   reports.is        — buildIncomeStatement() output
//   reports.bs        — buildBalanceSheet() output
//   reports.t12       — buildT12() output (optional, for revenue growth + runway)
//   reports.periodLabel — string e.g. "Four Months Ended April 30, 2026"
export function computeKpis({ is, bs, t12, periodLabel } = {}) {
  if (!is || !bs) return [];

  const t = is.totals || {};
  const py = is.prior || {};
  const sections = bs.sections || {};

  const totalRevenue   = t.totalRevenue   || 0;
  const totalCOGS      = t.totalCOGS      || 0;
  const grossProfit    = t.grossProfit    || 0;
  const totalOpEx      = t.totalOpEx      || 0;
  const ebit           = t.ebit           || 0;
  const netIncome      = t.netIncome      || 0;
  const pyRevenue      = py.totalRevenue  || 0;

  const cashBal        = sections.cash?.total       || 0;
  const arBal          = (() => {
    // AR is a current-asset canonical line. Pull from assetsCur items
    // matching "Accounts Receivable" / "Trade Receivables" / "A/R".
    const items = sections.assetsCur?.items || [];
    const ar = items.find(it =>
      it.kind === 'leaf' && /accounts?\s+receivable|trade\s+receivable|^a\/r\b/i.test(it.label || '')
    );
    return ar ? Math.abs(ar.amount || 0) : 0;
  })();
  const apBal          = (() => {
    const items = sections.liabCur?.items || [];
    const ap = items.find(it =>
      it.kind === 'leaf' && /accounts?\s+payable|trade\s+payable|^a\/p\b/i.test(it.label || '')
    );
    return ap ? Math.abs(ap.amount || 0) : 0;
  })();
  const inventoryBal   = (() => {
    const items = sections.assetsCur?.items || [];
    const inv = items.find(it =>
      it.kind === 'leaf' && /inventor/i.test(it.label || '')
    );
    return inv ? Math.abs(inv.amount || 0) : 0;
  })();

  const currentAssets  = (sections.cash?.total || 0) + (sections.assetsCur?.total || 0);
  const currentLiab    = (sections.liabCur?.total || 0) + (sections.debtShort?.total || 0);
  const totalLiab      = bs.totals?.totalLiab || 0;
  const totalEquity    = (bs.totals?.totalEquity || 0) + netIncome;
  const workingCapital = currentAssets - currentLiab;

  // EBITDA — approximate. EBIT + D&A if available on the IS; else
  // fall back to EBIT and flag in the sub-label.
  const dAndA = (is.sections?.opex?.items || [])
    .filter(it => /depreciation|amortization/i.test(it.label || ''))
    .reduce((s, it) => s + Math.abs(it.amount || 0), 0);
  const ebitda = ebit + dAndA;

  // T12 monthly opex for cash-runway calc (avg monthly burn).
  let avgMonthlyOpex = totalOpEx > 0 ? totalOpEx / (periodDays(periodLabel) / 30) : 0;
  if (t12?.sections) {
    const opexRow = t12.sections.find(s => /operating\s+expense/i.test(s.label || '')) || null;
    if (opexRow && Array.isArray(opexRow.monthsTotal)) {
      const valid = opexRow.monthsTotal.filter(v => v != null);
      if (valid.length > 0) {
        avgMonthlyOpex = valid.reduce((s, v) => s + Math.abs(v), 0) / valid.length;
      }
    }
  }

  const days = periodDays(periodLabel);

  // ── KPI rows ────────────────────────────────────────────────
  const kpis = [];

  // Liquidity
  kpis.push({
    id: 'current_ratio', group: 'liquidity', label: 'Current Ratio',
    value: currentLiab > 0 ? currentAssets / currentLiab : null,
    valueFormatted: currentLiab > 0 ? fmtRatio(currentAssets / currentLiab) : 'n/a',
    sub: currentLiab > 0 ? `${fmtMoney(currentAssets)} / ${fmtMoney(currentLiab)}` : 'No current liabilities',
    status: currentLiab > 0 ? scoreThreshold(currentAssets / currentLiab, 'current_ratio') : 'neutral',
    n_a: currentLiab > 0 ? null : 'No current liabilities — ratio undefined.',
  });
  kpis.push({
    id: 'quick_ratio', group: 'liquidity', label: 'Quick Ratio',
    value: currentLiab > 0 ? (currentAssets - inventoryBal) / currentLiab : null,
    valueFormatted: currentLiab > 0 ? fmtRatio((currentAssets - inventoryBal) / currentLiab) : 'n/a',
    sub: inventoryBal > 0
      ? `Excludes ${fmtMoney(inventoryBal)} inventory`
      : 'No inventory on books',
    status: currentLiab > 0 ? scoreThreshold((currentAssets - inventoryBal) / currentLiab, 'quick_ratio') : 'neutral',
  });
  kpis.push({
    id: 'working_capital', group: 'liquidity', label: 'Working Capital',
    value: workingCapital,
    valueFormatted: fmtMoney(workingCapital),
    sub: `${fmtMoney(currentAssets)} − ${fmtMoney(currentLiab)}`,
    status: workingCapital > 0 ? 'good' : workingCapital === 0 ? 'warn' : 'bad',
  });
  kpis.push({
    id: 'debt_to_equity', group: 'liquidity', label: 'Debt-to-Equity',
    value: totalEquity !== 0 ? totalLiab / totalEquity : null,
    valueFormatted: totalEquity !== 0 ? fmtRatio(totalLiab / totalEquity) : 'n/a',
    sub: `${fmtMoney(totalLiab)} liab / ${fmtMoney(totalEquity)} equity`,
    status: totalEquity > 0 ? scoreThreshold(totalLiab / totalEquity, 'debt_to_equity') : 'neutral',
    n_a: totalEquity > 0 ? null : 'Equity is zero or negative — ratio undefined.',
  });

  // Profitability
  kpis.push({
    id: 'gross_margin', group: 'profitability', label: 'Gross Margin',
    value: totalRevenue > 0 && totalCOGS > 0 ? grossProfit / totalRevenue : null,
    valueFormatted: totalRevenue > 0 && totalCOGS > 0
      ? fmtPct(grossProfit / totalRevenue)
      : 'n/a',
    sub: totalCOGS > 0
      ? `${fmtMoney(grossProfit)} on ${fmtMoney(totalRevenue)} revenue`
      : 'No COGS on books — gross margin not applicable',
    status: totalRevenue > 0 && totalCOGS > 0 ? scoreThreshold(grossProfit / totalRevenue, 'gross_margin') : 'neutral',
    n_a: totalCOGS > 0 ? null : 'No COGS recorded for this engagement.',
  });
  kpis.push({
    id: 'operating_margin', group: 'profitability', label: 'Operating Margin',
    value: totalRevenue > 0 ? ebit / totalRevenue : null,
    valueFormatted: totalRevenue > 0 ? fmtPct(ebit / totalRevenue) : 'n/a',
    sub: `${fmtMoney(ebit)} EBIT on ${fmtMoney(totalRevenue)} revenue`,
    status: totalRevenue > 0 ? scoreThreshold(ebit / totalRevenue, 'operating_margin') : 'neutral',
  });
  kpis.push({
    id: 'net_margin', group: 'profitability', label: 'Net Margin',
    value: totalRevenue > 0 ? netIncome / totalRevenue : null,
    valueFormatted: totalRevenue > 0 ? fmtPct(netIncome / totalRevenue) : 'n/a',
    sub: `${fmtMoney(netIncome)} net income`,
    status: totalRevenue > 0 ? scoreThreshold(netIncome / totalRevenue, 'net_margin') : 'neutral',
  });
  kpis.push({
    id: 'ebitda_margin', group: 'profitability', label: 'EBITDA Margin',
    value: totalRevenue > 0 ? ebitda / totalRevenue : null,
    valueFormatted: totalRevenue > 0 ? fmtPct(ebitda / totalRevenue) : 'n/a',
    sub: dAndA > 0
      ? `EBIT ${fmtMoney(ebit)} + D&A ${fmtMoney(dAndA)}`
      : `EBIT ${fmtMoney(ebit)} (no D&A on books)`,
    status: totalRevenue > 0 ? scoreThreshold(ebitda / totalRevenue, 'ebitda_margin') : 'neutral',
  });

  // Efficiency
  kpis.push({
    id: 'ar_days', group: 'efficiency', label: 'AR Days (DSO)',
    value: totalRevenue > 0 && arBal > 0 ? (arBal / totalRevenue) * days : null,
    valueFormatted: totalRevenue > 0 && arBal > 0
      ? fmtDays((arBal / totalRevenue) * days)
      : 'n/a',
    sub: arBal > 0
      ? `${fmtMoney(arBal)} AR ÷ ${fmtMoney(totalRevenue / days)}/day`
      : 'No AR on the books',
    status: totalRevenue > 0 && arBal > 0
      ? scoreThreshold((arBal / totalRevenue) * days, 'ar_days')
      : 'neutral',
    n_a: arBal > 0 ? null : 'No Accounts Receivable line on this engagement.',
  });
  kpis.push({
    id: 'ap_days', group: 'efficiency', label: 'AP Days (DPO)',
    value: totalOpEx > 0 && apBal > 0 ? (apBal / totalOpEx) * days : null,
    valueFormatted: totalOpEx > 0 && apBal > 0
      ? fmtDays((apBal / totalOpEx) * days)
      : 'n/a',
    sub: apBal > 0
      ? `${fmtMoney(apBal)} AP ÷ ${fmtMoney(totalOpEx / days)}/day opex`
      : 'No AP on the books',
    status: totalOpEx > 0 && apBal > 0
      ? scoreThreshold((apBal / totalOpEx) * days, 'ap_days')
      : 'neutral',
    n_a: apBal > 0 ? null : 'No Accounts Payable line on this engagement.',
  });
  kpis.push({
    id: 'cash_runway', group: 'efficiency', label: 'Cash Runway',
    value: avgMonthlyOpex > 0 ? cashBal / avgMonthlyOpex : null,
    valueFormatted: avgMonthlyOpex > 0 ? fmtMonths(cashBal / avgMonthlyOpex) : 'n/a',
    sub: avgMonthlyOpex > 0
      ? `${fmtMoney(cashBal)} cash ÷ ${fmtMoney(avgMonthlyOpex)}/mo burn`
      : 'No operating expenses to compute burn',
    status: avgMonthlyOpex > 0 ? scoreThreshold(cashBal / avgMonthlyOpex, 'cash_runway') : 'neutral',
  });

  // Growth
  kpis.push({
    id: 'revenue_growth', group: 'growth', label: 'Revenue Growth YoY',
    value: pyRevenue > 0 ? (totalRevenue - pyRevenue) / pyRevenue : null,
    valueFormatted: pyRevenue > 0
      ? fmtPct((totalRevenue - pyRevenue) / pyRevenue)
      : 'n/a',
    sub: pyRevenue > 0
      ? `${fmtMoney(totalRevenue)} vs ${fmtMoney(pyRevenue)} prior year`
      : 'No prior-year comparable',
    status: pyRevenue > 0 ? scoreThreshold((totalRevenue - pyRevenue) / pyRevenue, 'revenue_growth') : 'neutral',
    n_a: pyRevenue > 0 ? null : 'No prior-year revenue available for comparison.',
  });

  return kpis;
}

export const KPI_GROUPS = [
  { id: 'liquidity', label: 'Liquidity & Solvency' },
  { id: 'profitability', label: 'Profitability' },
  { id: 'efficiency', label: 'Efficiency' },
  { id: 'growth', label: 'Growth' },
];

// Silence unused-import lint for utility imports retained for future
// per-KPI rounding edge cases.
// eslint-disable-next-line no-unused-vars
const _round0 = round0;
